import { Show, useModal, useForm } from "@refinedev/antd";
import { useCustom } from "@refinedev/core";
import {
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import { useParams } from "react-router-dom";
import { useState } from "react";
import {
  errorNotification,
  successNotification,
} from "../../../helpers/notiffication.helper";
import axAPI from "../../../api/axios-instance";
import { useNavigation } from "@refinedev/core";
import type { UploadProps } from "antd";
import type { RcFile } from "antd/es/upload/interface";
import * as XLSX from "xlsx";
type SegmentUser = {
  userId: string;
  firstname: string;
  lastname: string;
};
type FoundUser = {
  id: string;
  firstName: string;
  lastName: string;
  patronymic?: string | null;
  phone?: string | null;
  email?: string | null;
};

export const SegmentUsersPage = () => {
  const { segmentId } = useParams();
  const { push } = useNavigation();

  const { data, isLoading, refetch } = useCustom<SegmentUser[] | any>({
    dataProviderName: "pushSegmentsDataProvider",
    url: "/cms/push/segment/users",
    method: "get",
    meta: { params: { id: segmentId } },
    ...errorNotification({}),
  });

  const usersRaw = data?.data as unknown;
  const users: SegmentUser[] = Array.isArray(usersRaw)
    ? (usersRaw as SegmentUser[])
    : [];

  const { modalProps, show, close } = useModal();
  const { formProps, form } = useForm<{ query: string }>({
    resource: "",
    dataProviderName: "pushSegmentsDataProvider",
    redirect: false,
    ...successNotification({
      successData: { message: "Пользователь добавлен", type: "success" },
      callback: () => {
        close();
        form.resetFields();
        refetch();
      },
    }),
    ...errorNotification({}),
  });

  // undefined → показать всех; строка → искать
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
  }>({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState<{
    added: number;
    skipped: number;
    errors: string[];
  }>({
    added: 0,
    skipped: 0,
    errors: [],
  });
  const { data: foundUserData, isFetching } = useCustom<
    FoundUser | FoundUser[]
  >({
    dataProviderName: "pushSegmentsDataProvider",
    url: "/cms/push/user/search",
    method: "get",
    meta: searchQuery ? { params: { query: searchQuery } } : undefined,
    queryOptions: { enabled: !!searchQuery },
    ...errorNotification({}),
  });

  const raw = foundUserData?.data as FoundUser | FoundUser[] | undefined;
  let foundUsers: FoundUser[] = [];
  if (Array.isArray(raw)) {
    foundUsers = raw;
  } else if (raw) {
    foundUsers = [raw];
  }

  const handleSearch = async () => {
    const query = form.getFieldValue("query");
    setSearchQuery(query || undefined);
  };

  // задержка между запросами, чтобы бэк не блокировал
  const REQUEST_DELAY_MS = 1000;
  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const normalizePhone = (value: unknown) => {
    const digits = String(value ?? "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.slice(1);
  };

  const parsePhonesFromFile = async (file: RcFile) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return [];
    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      blankrows: false,
    });

    const phones = rows
      .map((row) => (Array.isArray(row) ? row[0] : undefined))
      .filter((cell) => cell !== undefined && cell !== null)
      .map((cell) => normalizePhone(cell));

    return Array.from(new Set(phones.filter(Boolean))) as string[];
  };

  const handleImportFile: UploadProps["beforeUpload"] = async (file) => {
    if (!segmentId) {
      message.error("Сегмент не найден");
      return false;
    }

    setIsImporting(true);
    setImportResult({ added: 0, skipped: 0, errors: [] });
    setImportProgress({ current: 0, total: 0 });

    try {
      const phones = await parsePhonesFromFile(file as RcFile);

      if (!phones.length) {
        message.warning("В файле не найдено номеров телефонов");
        return false;
      }

      let added = 0;
      let skipped = 0;
      const errors: string[] = [];

      setImportProgress({ current: 0, total: phones.length });

      for (let i = 0; i < phones.length; i++) {
        const phone = phones[i];

        // задержка между запросами, чтобы бэк не блокировал (кроме самого первого)
        if (i > 0) {
          await sleep(REQUEST_DELAY_MS);
        }

        try {
          const searchResponse = await axAPI.get("/cms/push/user/search", {
            params: { query: phone },
          });
          const found = searchResponse.data as FoundUser | FoundUser[] | null;
          const user = Array.isArray(found) ? found[0] : found;

          if (user?.id) {
            // пауза между search и add
            await sleep(REQUEST_DELAY_MS);
            await axAPI.get("/cms/push/user/add", {
              params: { userId: user.id, segmentId },
            });
            added += 1;
          } else {
            skipped += 1;
            errors.push(`Пользователь с номером ${phone} не найден`);
          }
        } catch (err) {
          skipped += 1;
          errors.push(`Ошибка при обработке номера ${phone}`);
        }

        setImportProgress({ current: i + 1, total: phones.length });
      }

      setImportResult({ added, skipped, errors });

      message.success(
        `Импорт завершён: добавлено ${added}, пропущено ${skipped}`
      );
      await refetch();
    } catch (err) {
      message.error("Не удалось обработать файл. Проверьте формат xls/xlsx.");
    } finally {
      setIsImporting(false);
    }

    return false;
  };

  const uploadProps: UploadProps = {
    accept: ".xls,.xlsx",
    showUploadList: false,
    beforeUpload: handleImportFile,
  };

  const handleAddUser = async (userId: string) => {
    if (!userId || !segmentId) return;
    await axAPI.get("/cms/push/user/add", { params: { userId, segmentId } });
    await refetch();
    message.success("Пользователь добавлен");
    close();
  };

  const handleRemoveUser = async (userId: string) => {
    if (!userId || !segmentId) return;
    await axAPI.get("/cms/push/user/remove", { params: { userId, segmentId } });
    await refetch();
    message.success("Пользователь удалён из сегмента");
  };

  const handleRemoveSegment = async () => {
    if (!segmentId) return;
    try {
      await axAPI.get("/cms/push/segment/remove", {
        params: { id: segmentId },
      });
      message.success("Сегмент удалён");
      push("/segments");
    } catch (error: any) {
      // Системный сегмент бэкенд удалять не даёт (409) — раньше ошибка глоталась,
      // и пользователю показывалось «Сегмент удалён», хотя он оставался на месте.
      const text =
        typeof error?.message === "string"
          ? error.message
          : "Не удалось удалить сегмент";
      message.error(text);
    }
  };

  return (
    <Show
      title={"Пользователи сегмента"}
      breadcrumb={false}
      headerButtons={
        <Space>
          <Upload {...uploadProps}>
            <Button loading={isImporting}>Импорт XLS/XLSX</Button>
          </Upload>
          <Button onClick={show}>Добавить пользователя</Button>
          <Button danger onClick={handleRemoveSegment}>
            Удалить сегмент
          </Button>
        </Space>
      }
    >
      <Space
        direction="vertical"
        size="small"
        style={{ marginBottom: 16, width: "100%" }}
      >
        <Typography.Text type="secondary">
          Файл: один столбец с телефонами. Без заголовка колонки номера начиная
          с 8 или 7
        </Typography.Text>
        {isImporting && importProgress.total > 0 && (
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Progress
              percent={Math.round(
                (importProgress.current / importProgress.total) * 100
              )}
              status="active"
            />
            <Typography.Text type="secondary">
              Обработано {importProgress.current} из {importProgress.total}
            </Typography.Text>
          </Space>
        )}
        {(importResult.added > 0 ||
          importResult.skipped > 0 ||
          importResult.errors.length > 0) && (
          <Typography.Text>
            Добавлено: {importResult.added}, пропущено: {importResult.skipped}
            {importResult.errors.length > 0 && (
              <>
                {" "}
                | Ошибки:{" "}
                {importResult.errors.length > 3
                  ? `${importResult.errors.slice(0, 3).join("; ")} …`
                  : importResult.errors.join("; ")}
              </>
            )}
          </Typography.Text>
        )}
      </Space>
      {isLoading ? (
        <Spin />
      ) : (
        <Table<SegmentUser>
          dataSource={users}
          rowKey={(r) => r.userId}
          pagination={false}
        >
          <Table.Column<SegmentUser>
            title={"Фамилия"}
            key={"lastname"}
            render={(_, r) => <Typography.Text>{r.lastname}</Typography.Text>}
          />
          <Table.Column<SegmentUser>
            title={"Имя"}
            key={"firstname"}
            render={(_, r) => <Typography.Text>{r.firstname}</Typography.Text>}
          />
          <Table.Column<SegmentUser>
            title={"Действие"}
            key={"action"}
            render={(_, r) => (
              <Button
                danger
                size="small"
                type="primary"
                onClick={() => {
                  handleRemoveUser(r.userId);
                  close();
                }}
              >
                Удалить
              </Button>
            )}
          />
        </Table>
      )}
      <Modal
        {...modalProps}
        title={"Добавление пользователя"}
        onCancel={close}
        footer={<></>}
      >
        {/* onFinish после formProps: Enter в поле сабмитит форму, и рефайновский
            onFinish уходил бы в segment/create — здесь сабмит означает «Найти» */}
        <Form {...formProps} onFinish={handleSearch} layout={"vertical"}>
          <Form.Item
            name="query"
            label={"Телефон (79…) или email"}
            rules={[{ required: true, message: "Укажите телефон или email" }]}
          >
            <Space>
              <Input placeholder="79... или email" />
              <Button
                onClick={handleSearch}
                loading={isFetching}
                type="primary"
              >
                Найти
              </Button>
            </Space>
          </Form.Item>
          <Table<FoundUser>
            dataSource={foundUsers}
            rowKey={(r) => r.id}
            loading={isFetching}
            pagination={false}
          >
            <Table.Column<FoundUser>
              title={"Фамилия"}
              key={"lastName"}
              render={(_, r) => <Typography.Text>{r.lastName}</Typography.Text>}
            />
            <Table.Column<FoundUser>
              title={"Имя"}
              key={"firstName"}
              render={(_, r) => (
                <Typography.Text>{r.firstName}</Typography.Text>
              )}
            />
            <Table.Column<FoundUser>
              title={"Контакты"}
              key={"contacts"}
              render={(_, r) => (
                <Typography.Text type="secondary">
                  {r.phone || r.email || "–"}
                </Typography.Text>
              )}
            />
            <Table.Column<FoundUser>
              title={"Действие"}
              key={"action"}
              render={(_, r) => (
                <Button type="primary" onClick={() => handleAddUser(r.id)}>
                  Добавить
                </Button>
              )}
            />
          </Table>
        </Form>
      </Modal>
    </Show>
  );
};
